import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AdminProfile, AdminRole, LoginResponse } from './models';

const TOKEN_KEY = 'comuni_admin_token';
const PROFILE_KEY = 'comuni_admin_profile';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);

  private readonly _token = signal<string | null>(localStorage.getItem(TOKEN_KEY));
  private readonly _profile = signal<AdminProfile | null>(this.readProfile());

  readonly profile = this._profile.asReadonly();
  readonly isAuthenticated = computed(() => !!this._token());

  /** Roles con permisos de escritura (crear/editar/eliminar). */
  readonly canManage = computed(() => {
    const role = this._profile()?.role;
    return role === 'admin' || role === 'superadmin';
  });

  readonly isSuperadmin = computed(() => this._profile()?.role === 'superadmin');

  get token(): string | null {
    return this._token();
  }

  hasRole(...roles: AdminRole[]): boolean {
    const role = this._profile()?.role;
    return !!role && roles.includes(role);
  }

  login(email: string, password: string): Observable<LoginResponse> {
    return this.http
      .post<LoginResponse>(`${environment.apiUrl}/admin/auth/login`, { email, password })
      .pipe(
        tap((res) => {
          this._token.set(res.accessToken);
          this._profile.set(res.admin);
          localStorage.setItem(TOKEN_KEY, res.accessToken);
          localStorage.setItem(PROFILE_KEY, JSON.stringify(res.admin));
        }),
      );
  }

  /**
   * Completa el login iniciado con GitHub. El backend redirige al panel con un JWT en
   * la URL (?token=...); acá lo persistimos y traemos el perfil desde `/admin/auth/me`
   * (el interceptor ya adjunta el token porque lo guardamos antes de la llamada).
   */
  completeGithubLogin(token: string): Observable<AdminProfile> {
    this._token.set(token);
    localStorage.setItem(TOKEN_KEY, token);
    return this.http.get<AdminProfile>(`${environment.apiUrl}/admin/auth/me`).pipe(
      tap((profile) => {
        this._profile.set(profile);
        localStorage.setItem(PROFILE_KEY, JSON.stringify(profile));
      }),
    );
  }

  logout(): void {
    this._token.set(null);
    this._profile.set(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(PROFILE_KEY);
    this.router.navigate(['/login']);
  }

  /** Solicita el envío del correo de recupero. Responde 200 exista o no el email. */
  forgotPassword(email: string): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${environment.apiUrl}/admin/auth/forgot-password`, {
      email,
    });
  }

  /** Define una nueva contraseña a partir del token del correo (activación o recupero). */
  resetPassword(token: string, password: string): Observable<{ ok: boolean }> {
    return this.http.post<{ ok: boolean }>(`${environment.apiUrl}/admin/auth/reset-password`, {
      token,
      password,
    });
  }

  /** Valida el token del correo sin consumirlo (para renderizar la pantalla de definir contraseña). */
  validateResetToken(token: string): Observable<{ valid: boolean; email?: string; name?: string }> {
    return this.http.get<{ valid: boolean; email?: string; name?: string }>(
      `${environment.apiUrl}/admin/auth/reset-password/validate`,
      { params: { token } },
    );
  }

  private readProfile(): AdminProfile | null {
    const raw = localStorage.getItem(PROFILE_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as AdminProfile;
    } catch {
      return null;
    }
  }
}
